import { nextTick, type ComputedRef } from "vue";
import type { TabItem } from "@/components/Base/BaseTab/BaseTab.vue";

interface Props {
  modelValue: string;
  tabs: TabItem[];
}

export const useTabKeyboardNavigation = (
  props: Props,
  enabledTabs: ComputedRef<TabItem[]>,
  selectTab: (value: string) => void,
) => {
  const handleKeyDown = (event: KeyboardEvent, tabIndex: number): void => {
    const { key } = event;

    // Handle Enter and Space key activation
    if (key === "Enter" || key === " ") {
      event.preventDefault();
      const tab = props.tabs[tabIndex];
      if (tab && !tab.disabled) {
        selectTab(tab.value);
      }
      return;
    }

    // Handle arrow key navigation
    if (key === "ArrowRight" || key === "ArrowLeft") {
      event.preventDefault();
      navigateToAdjacentTab(key === "ArrowRight" ? 1 : -1);
    }
  };

  const navigateToAdjacentTab = (direction: 1 | -1): void => {
    const enabledTabsOnly = enabledTabs.value;
    const currentIndex = enabledTabsOnly.findIndex(
      (tab) => tab.value === props.modelValue,
    );

    if (currentIndex === -1) return;

    const nextIndex =
      (currentIndex + direction + enabledTabsOnly.length) %
      enabledTabsOnly.length;
    const nextTab = enabledTabsOnly[nextIndex];

    if (!nextTab) return;

    // Activate and focus the next tab
    selectTab(nextTab.value);
    focusTab(nextTab.value);
  };

  const focusTab = (tabValue: string): void => {
    nextTick(() => {
      const tabElement = document.querySelector(
        `[data-pc-name="tab"][aria-controls="panel-${tabValue}"]`,
      ) as HTMLElement;
      tabElement?.focus();
    });
  };

  return {
    handleKeyDown,
  };
};
